// frontend/src/components/admin/Dashboard.js
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FiActivity, FiCalendar, FiChevronRight, FiDollarSign, FiEye, FiMail, FiMoreVertical, FiTrendingUp, FiUsers, FiZap } from "react-icons/fi";
import { useAnnouncements } from "../../contexts/AnnouncementsContext";
import { useContact } from "../../contexts/ContactContext";
import { useEvents } from "../../contexts/EventsContext";
import { usePayment } from "../../contexts/PaymentContext";
import { useUpdates } from "../../contexts/updatesContext";
import CalendarWidget from "./CalenderWidget";
import RecentAnnouncements from "./RecentAnnouncements";
import RecentMessages from "./RecentMessages";
import RecentPayments from "./RecentPayments";
import RecentUploads from "./RecentUploads";

const DashboardHome = () => {
  // Real data hooks - preserved exactly as original
  const { events } = useEvents();
  const { messages } = useContact();
  const { offerings } = usePayment();
  const { announcements } = useAnnouncements();
  const { updates } = useUpdates();

  // New state for modern features
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);

  // Real-time clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate stats from real data - preserved exactly as original
  const stats = [
    {
      title: "Upcoming Events",
      value: events?.filter(e => new Date(e.date) > new Date()).length || 0,
      icon: <FiCalendar className="h-6 w-6" />,
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
      change: "+12%",
      trend: "up"
    },
    {
      title: "Total Donations",
      value: `R${offerings?.reduce((sum, o) => sum + o.amount, 0).toFixed(2) || 0}`,
      icon: <FiDollarSign className="h-6 w-6" />,
      color: "from-emerald-500 to-green-500",
      bgColor: "bg-gradient-to-br from-emerald-50 to-green-50",
      change: "+8.2%",
      trend: "up"
    },
    {
      title: "New Messages",
      value: messages?.filter(m => m.status === 'new').length || 0,
      icon: <FiMail className="h-6 w-6" />,
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
      change: "+5.1%",
      trend: "up"
    },
    {
      title: "Active Members",
      value: announcements?.filter(a => new Date(a.publishDate) <= new Date()).length || 0,
      icon: <FiUsers className="h-6 w-6" />,
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
      change: "+3.7%",
      trend: "up"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingVariants = {
    initial: { y: 0 },
    animate: { 
      y: [-5, 5, -5],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          variants={floatingVariants}
          initial="initial"
          animate="animate"
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"
        />
        <motion.div
          variants={floatingVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 2 }}
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl"
        />
      </div>

      {/* Modern Header with Neumorphism */}
      <motion.div
        variants={itemVariants}
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200/50 shadow-lg"
      >
        <div className="flex justify-between items-center px-8 py-6">
          <div className="flex items-center space-x-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg"
            >
              <FiZap className="h-6 w-6 text-white" />
            </motion.div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
                Dashboard Overview
              </h1>
              <p className="text-gray-600 mt-1 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                System operational • All services running
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Auto-refresh toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsAutoRefresh(!isAutoRefresh)}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                isAutoRefresh 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              <FiActivity className="h-4 w-4 mr-2 inline" />
              Auto-refresh {isAutoRefresh ? 'ON' : 'OFF'}
            </motion.button>
            
            {/* Real-time clock */}
            <div className="text-right bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="text-sm font-bold text-gray-900">
                {currentTime.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="text-2xl font-mono font-bold text-blue-600">
                {currentTime.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="px-8 py-8 space-y-8 relative z-10">
        {/* Modern Stats Grid with Glassmorphism */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.03,
                y: -10,
                transition: { type: "spring", stiffness: 400, damping: 20 }
              }}
              onClick={() => setSelectedMetric(selectedMetric === index ? null : index)}
              className={`group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer ${
                selectedMetric === index ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-transparent' : ''
              }`}
            >
              {/* Dynamic gradient background */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-br ${stat.color}`} />
              
              {/* Animated border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-4 rounded-2xl ${stat.bgColor} group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <div className={`text-transparent bg-gradient-to-r ${stat.color} bg-clip-text`}>
                      {stat.icon}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="p-1 rounded-full bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FiEye className="h-4 w-4 text-gray-600" />
                    </motion.div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    {stat.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-bold text-gray-900">
                      {stat.value}
                    </p>
                    <FiChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                </div>

                {/* Expanded detail view */}
                <motion.div
                  initial={false}
                  animate={{ 
                    height: selectedMetric === index ? 'auto' : 0,
                    opacity: selectedMetric === index ? 1 : 0
                  }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      Trend: <span className="font-semibold text-green-600">Positive</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Last updated: {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - Primary Content */}
          <motion.div variants={itemVariants} className="xl:col-span-2 space-y-8">
            {/* Calendar Widget with Modern Design */}
            <div className="group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg">
                      <FiCalendar className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Upcoming Events
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {events?.filter(e => new Date(e.date) > new Date()).length || 0} events scheduled
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
                  >
                    <span>View All</span>
                    <FiChevronRight className="h-4 w-4" />
                  </motion.button>
                </div>
                <div className="bg-white/50 rounded-2xl p-6 backdrop-blur-sm">
                  <CalendarWidget events={events} />
                </div>
              </div>
            </div>

            {/* Announcements with Modern Card Design */}
            <div className="group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg">
                      <FiTrendingUp className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Latest Announcements
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {announcements?.length || 0} total announcements
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
                  >
                    <span>Manage</span>
                    <FiMoreVertical className="h-4 w-4" />
                  </motion.button>
                </div>
                <div className="bg-white/50 rounded-2xl p-6 backdrop-blur-sm">
                  <RecentAnnouncements />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Secondary Content with Card Stack */}
          <motion.div variants={itemVariants} className="xl:col-span-1 space-y-6">
            {/* Messages Card */}
            <div className="group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
                      <FiMail className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Messages
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-md" />
                    <span className="text-sm font-semibold text-red-600">
                      {messages?.filter(m => m.status === 'new').length || 0} new
                    </span>
                  </div>
                </div>
                <div className="bg-white/50 rounded-xl p-4 backdrop-blur-sm">
                  <RecentMessages />
                </div>
              </div>
            </div>

            {/* Uploads Card */}
            <div className="group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-md">
                      <FiActivity className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Recent Uploads
                    </h3>
                  </div>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    className="w-2 h-2 bg-cyan-500 rounded-full"
                  />
                </div>
                <div className="bg-white/50 rounded-xl p-4 backdrop-blur-sm">
                  <RecentUploads />
                </div>
              </div>
            </div>

            {/* Donations Card */}
            <div className="group relative overflow-hidden rounded-3xl backdrop-blur-sm bg-white/70 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md">
                      <FiDollarSign className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Recent Donations
                    </h3>
                  </div>
                  <div className="text-sm font-semibold text-emerald-600">
                    R{offerings?.reduce((sum, o) => sum + o.amount, 0).toFixed(2) || 0}
                  </div>
                </div>
                <div className="bg-white/50 rounded-xl p-4 backdrop-blur-sm">
                  <RecentPayments />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardHome;